<template>
  <div>
    <o-chip v-if="isStatusConfirmed" flat class="mb-1" variant="success">
      {{ $t('reservation.confirmed') }}
    </o-chip>
    <o-chip v-if="isStatusUnconfirmed" flat class="mb-1" variant="warning">
      {{ $t('reservation.unconfirmed') }}
    </o-chip>
    <o-chip v-if="isStatusCancelled" flat class="mb-1" variant="danger">
      {{ $t('general.cancelled') }}

      <span v-if="_reservationCustomer.cancellationType">
        : {{ _reservationCustomer.cancellationType.shortName }}
      </span>

      <span v-if="_reservationCustomer.cancellationNote" class="ml-1">
        ({{ _reservationCustomer.cancellationNote }})
      </span>
    </o-chip>

    <o-chip v-if="isDeleted" flat class="mb-1" variant="danger">
      {{ $t('general.deleted') }}
    </o-chip>

    <o-chip v-if="isPaid" flat class="mb-1" variant="success">
      {{ $t('general.paid') }}
    </o-chip>

    <o-chip
      v-if="isCheckedIn"
      flat
      class="mb-1"
      variant="info"
    >
      {{ $t('reservation.checkedIn') }}
    </o-chip>

    <o-chip
      v-if="isNoShow"
      flat
      class="mb-1"
      variant="warning"
    >
      {{ $t('reservation.noShow') }}
    </o-chip>

    <o-chip v-if="isBookedOnline" flat variant="info" class="mb-1">
      {{ $t('general.online') }}
    </o-chip>

    <o-chip v-if="isCustomerConfirmed" flat class="mb-1" variant="success">
      {{ $t('reservation.attending') }}
    </o-chip>

    <o-chip v-if="isPaymentDetailsCaptured" flat class="mb-1" variant="success">
      {{ $t('reservation.paymentDetailsCaptured') }}
    </o-chip>

    <o-chip v-if="onlineTermsAgreed" flat class="mb-1" variant="success">
      {{ $t('reservation.termsAgreed') }}
    </o-chip>

    <o-chip v-if="hasBouncedEmail" flat class="mb-1" variant="warning">
      {{ $t('general.emailBounced') }}
    </o-chip>

    <o-chip v-if="reservationCustomer.makeupDetails?.isMakeup" flat class="mb-1" variant="info">
      {{ $t('components.reservation.edit.attendee.makeup') }}
    </o-chip>

    <o-panel-link
      v-if="reservationCustomer.makeupDetails?.scheduledReservationCustomer"
      :path="`/appointment/attendee/${reservationCustomer.makeupDetails.scheduledReservationCustomer.idReservationCustomer}`"
      @click.stop
    >
      <o-chip flat class="mb-1" variant="info">
        {{ $t('components.reservation.edit.attendee.makeupOn', { date: $date.convert(reservationCustomer.makeupDetails.scheduledReservationCustomer.reservation?.date, undefined, $date.presets.short) }) }}
      </o-chip>
    </o-panel-link>

    <o-button
      v-if="reservationCustomer.makeupDetails?.makeupAvailable && reservationCustomer.customerCourse"
      size="sm"
      class="mb-1"
      @click="handleBookMakeup"
    >
      {{ $t('pages.customer.course.id.scheduleMakeup') }}
    </o-button>

    <book-makeup-dialog
      v-if="reservationCustomer.customerCourse"
      ref="bookMakeupDialog"
      :portal="panel.portal"
      :loading="loading"
      @update:loading="$emit('update:loading', $event)"
      @success="handleBookMakeupSuccess"
    />

    <status-request-type v-model="_reservationCustomer.customerRequestType" />

    <status-custom v-model="reservationCustomer.statusType" :reservation="_reservationCustomer" />
  </div>
</template>

<script setup lang="ts">
import StatusCustom from '@/components/reservation/elements/StatusCustom.vue'
import StatusRequestType from '@/components/reservation/elements/StatusRequestType.vue'
import BookMakeupDialog from '@/components/customerCourse/actions/BookMakeup.vue'

interface Props {
  reservationCustomer: Record<string, any>
  loading?: boolean
}

interface Emits {
  (e: 'update:reservationCustomer', value: any): void
  (e: 'update:loading', value: boolean): void
  (e: 'refresh'): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<Emits>()
const panel = inject<any>('panel')
const bookMakeupDialog = ref<any>()

const _reservationCustomer = computed({
  get () {
    return props.reservationCustomer
  },
  set (value) {
    emit('update:reservationCustomer', value)
  },
})

const isStatusConfirmed = computed(() => props.reservationCustomer.isStatusConfirmed)
const isStatusUnconfirmed = computed(() => props.reservationCustomer.isStatusUnconfirmed)
const isStatusCancelled = computed(() => props.reservationCustomer.isStatusCancelled)
const isCheckedIn = computed(() => props.reservationCustomer.checkedIn)
const isNoShow = computed(() => props.reservationCustomer.noShow)
const isDeleted = computed(() => props.reservationCustomer.deleted)
const isPaid = computed(() => props.reservationCustomer.isPaid)
const isBookedOnline = computed(() => props.reservationCustomer.reservation.wasBookedOnline)
const isCustomerConfirmed = computed(() => props.reservationCustomer.customerConfirmed)
const onlineTermsAgreed = computed(() => props.reservationCustomer.onlineTermsAgreed)
const isPaymentDetailsCaptured = computed(() => props.reservationCustomer.paymentDetailsCaptured)
const hasPass = computed(() => !!props.reservationCustomer.customerPass)
const hasBouncedEmail = computed(() => !!props.reservationCustomer.customer.emailBounced)

function handleBookMakeup () {
  bookMakeupDialog.value.open(
    props.reservationCustomer.customerCourse,
    props.reservationCustomer.idReservationCustomer,
  )
}

function handleBookMakeupSuccess () {
  emit('refresh')
}
</script>
